import React, { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import KpiLineRaceChartCard from "./KpiLineRaceChartCard";
import { KpiStatisticsDataGridProps } from "./Types";
import { getKpiColumns } from "./utils/kpiUtils";
import { Translate } from "@/app/application/Translate";
import { getCardBorderStyle } from "@/app/application/theme";
import { buildEndpointWithQueryParameters } from "@/data-adapter/SATOSPortal";
import { TrainingKpiStatistic } from "@/features/training/model/Training/TrainingKpiStatistic";
import {
	Box,
	Card,
	CardContent,
	Chip,
	Grid,
	Skeleton,
	Stack,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import { DataGrid, GridRowParams, GridToolbar } from "@mui/x-data-grid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { useFetchKpiStatistics } from "@/service/TrainingQueries";

dayjs.extend(utc);

const KPI_PANEL_MIN_HEIGHT = 520;
const KPI_HEADER_MIN_HEIGHT = 92;

interface AggregatedKpiRow {
	id: number;
	Id: number;
	Name: string;
	Average: number;
	Count: number;
	Required: number;
}

export const KpiStatisticsDataGridDashboard: FC<KpiStatisticsDataGridProps> = ({
	startDate,
	endDate,
	selectedRange,
	onRangeChange,
}) => {
	const navigate = useNavigate();
	const theme = useTheme();
	const panelSx = {
		borderRadius: { xs: 2, sm: 3 },
		border: getCardBorderStyle(theme),
		backgroundColor: theme.palette.background.paper,
		overflow: "hidden",
		boxShadow:
			theme.palette.mode === "dark"
				? "0 1px 0 0 rgba(255,255,255,0.04)"
				: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
	};
	const [paginationModel, setPaginationModel] = useState({
		pageSize: 5,
		page: 0,
	});

	const queryOptions = {
		StartDate: startDate ? dayjs(startDate).format("YYYY.MM.DD") : undefined,
		EndDate: endDate ? dayjs(endDate).format("YYYY.MM.DD") : undefined,
	};
	const archiveQueryOptions = {
		StartDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : undefined,
		EndDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : undefined,
	};

	const columns = useMemo(() => getKpiColumns(), []);
	const { isLoading, data: kpiStatistics } =
		useFetchKpiStatistics(queryOptions);
	const kpiRows = useMemo(
		() => (Array.isArray(kpiStatistics) ? kpiStatistics : []),
		[kpiStatistics],
	);

	// We no longer render the old fullscreen LineChart here – KpiLineRaceChartCard
	// is the primary analytics visualization.

	const rows = useMemo<AggregatedKpiRow[]>(() => {
		const byId = new Map<
			number,
			{
				Id: number;
				Name: string;
				Average: number;
				Count: number;
				Required: number;
				weightedSum: number;
			}
		>();

		for (const item of kpiRows) {
			if (typeof item?.Id !== "number") continue;
			const safeName = typeof item.Name === "string" ? item.Name : "";
			const safeAverage = typeof item.Average === "number" ? item.Average : 0;
			const safeCount = typeof item.Count === "number" ? item.Count : 0;
			const safeRequired =
				typeof item.Required === "number" ? item.Required : 0;
			const existing = byId.get(item.Id);
			if (!existing) {
				byId.set(item.Id, {
					Id: item.Id,
					Name: safeName,
					Average: safeAverage,
					Count: safeCount,
					Required: safeRequired,
					weightedSum: safeAverage * safeCount,
				});
				continue;
			}

			existing.Count += safeCount;
			existing.weightedSum += safeAverage * safeCount;
			existing.Average =
				existing.Count > 0 ? existing.weightedSum / existing.Count : 0;
			existing.Required = safeRequired || existing.Required;
		}

		return Array.from(byId.values())
			.map((row) => ({
				id: row.Id,
				Id: row.Id,
				Name: row.Name,
				Average: row.Average,
				Count: row.Count,
				Required: row.Required,
			}))
			.sort((a, b) => String(a.Name ?? "").localeCompare(String(b.Name ?? "")));
	}, [kpiRows]);

	// Prepare chart-friendly items, including any Date/Required fields the API returns.
	const chartItems = kpiRows.map((k) => {
		const row = k as TrainingKpiStatistic & {
			Date?: string | null;
			Required?: number;
			Target?: number;
		};
		return {
			Name: row.Name,
			Date: row.Date ?? null,
			Average: row.Average,
			Count: row.Count,
			Required: row.Required ?? row.Target ?? undefined,
		};
	});

	const periodLabel =
		startDate && endDate
			? `${dayjs(startDate).format("DD/MM/YY")} → ${dayjs(endDate).format("DD/MM/YY")}`
			: null;
	const totalKpis = rows.length;
	
	const handleKpiRowClick = (params: GridRowParams<AggregatedKpiRow>) => {
		const kpiId = params.row.Id;
		if (!kpiId) return;

		const to = buildEndpointWithQueryParameters(
			`/training/archive/kpiParameters/${kpiId}`,
			archiveQueryOptions,
		);
		navigate(to);
	};

	if (isLoading) {
		return (
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Card
						variant="outlined"
						sx={{
							minHeight: 260,
							...panelSx,
						}}
					>
						<Box
							sx={(t) => ({
								py: 1.25,
								px: 2,
								borderBottom: 1,
								borderColor: "divider",
								bgcolor: t.palette.mode === "dark" ? "action.hover" : "grey.50",
							})}
						>
							<Skeleton variant="text" width={180} height={20} />
							<Skeleton
								variant="text"
								width={120}
								height={16}
								sx={{ mt: 0.5 }}
							/>
						</Box>
						<CardContent sx={{ pt: 1.5, px: 2 }}>
							<Skeleton
								variant="rectangular"
								height={40}
								sx={{ mb: 1, borderRadius: 1 }}
							/>
							<Skeleton
								variant="rectangular"
								height={40}
								sx={{ mb: 1, borderRadius: 1 }}
							/>
							<Skeleton
								variant="rectangular"
								height={40}
								sx={{ mb: 1, borderRadius: 1 }}
							/>
							<Skeleton
								variant="rectangular"
								height={40}
								sx={{ borderRadius: 1 }}
							/>
						</CardContent>
					</Card>
				</Grid>
				<Grid item xs={12} md={6}>
					<Card
						variant="outlined"
						sx={{
							minHeight: 260,
							...panelSx,
						}}
					>
						<Box
							sx={(t) => ({
								py: 1.25,
								px: 2,
								borderBottom: 1,
								borderColor: "divider",
								bgcolor: t.palette.mode === "dark" ? "action.hover" : "grey.50",
							})}
						>
							<Skeleton variant="text" width={140} height={20} />
						</Box>
						<CardContent sx={{ pt: 1.5, px: 2 }}>
							<Skeleton
								variant="rectangular"
								height={200}
								sx={{ borderRadius: 1 }}
							/>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		);
	}

	return (
		<Grid container spacing={2}>
			<Grid item xs={12} md={6}>
				<Card
					variant="outlined"
					sx={{
						...panelSx,
						display: "flex",
						flexDirection: "column",
						height: KPI_PANEL_MIN_HEIGHT,
						minHeight: KPI_PANEL_MIN_HEIGHT,
					}}
				>
					{/* Header strip – mirrors "Procedure list / Browse by group" */}
						<Box
							sx={(t) => ({
								py: 0.85,
								px: 2.25,
							minHeight: KPI_HEADER_MIN_HEIGHT,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 1.25,
							flexWrap: "wrap",
								bgcolor:
									t.palette.mode === "dark"
										? alpha(t.palette.primary.main, 0.12)
										: alpha(t.palette.primary.main, 0.05),
								borderBottom: 1,
								borderColor: alpha(t.palette.divider, 0.85),
								borderRadius: "12px 12px 0 0",
						})}
					>
						<Box sx={{ minWidth: 0, flex: 1 }}>
							<Typography
								variant="caption"
								sx={{
									display: "block",
									mb: 0.05,
									color: "primary.main",
									fontWeight: 700,
									letterSpacing: "0.08em",
									textTransform: "uppercase",
								}}
							>
								<Translate String="Auto.Features.Training.Pages.Dashboard.KpiStatisticsDataGridDashboard.L304C9.Analytics">Analytics</Translate>
							</Typography>
							<Typography
								variant="subtitle1"
								component="h3"
								sx={{
									fontWeight: 700,
									lineHeight: 1.05,
									letterSpacing: "-0.01em",
								}}
							>
								<Translate String="Training.Dashboard.Kpi.StatisticsTitle">
									KPI statistics
								</Translate>
							</Typography>
							{periodLabel && (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ display: "block", mt: 0 }}
								>
									{periodLabel}
								</Typography>
							)}
						</Box>

						<Stack
							direction="row"
							spacing={0.75}
							sx={{
								maxWidth: "100%",
								overflowX: "auto",
								overflowY: "hidden",
								whiteSpace: "nowrap",
								flexWrap: "nowrap",
								pb: 0.25,
								justifyContent: { xs: "flex-start", sm: "flex-end" },
								"&::-webkit-scrollbar": { height: 6 },
								"&::-webkit-scrollbar-thumb": {
									backgroundColor: alpha(theme.palette.text.primary, 0.18),
									borderRadius: 999,
								},
								"&::-webkit-scrollbar-track": {
									background: "transparent",
								},
							}}
						>
							<Chip
								size="small"
								label={
									<Stack direction="row" spacing={0.75} alignItems="center">
										<Typography variant="caption" color="text.secondary" fontWeight={800}>
											<Translate String="Auto.Features.Training.Pages.Dashboard.KpiStatisticsDataGridDashboard.L356C81.KPIs">KPIs</Translate>
										</Typography>
										<Typography variant="caption" fontWeight={950}>
											{totalKpis}
										</Typography>
									</Stack>
								}
								sx={{
									height: 26,
									borderRadius: 2,
									bgcolor: alpha(
										theme.palette.primary.main,
										theme.palette.mode === "dark" ? 0.16 : 0.09,
									),
									"& .MuiChip-label": { px: 1 },
								}}
							/>
						</Stack>
					</Box>

					<CardContent
						sx={{
							flex: 1,
							minHeight: 0,
							display: "flex",
							flexDirection: "column",
							pt: 1.35,
							px: 2,
							pb: 2,
						}}
					>
						<Box
							sx={{
								flex: 1,
								minHeight: 0,
								height: "100%",
								display: "flex",
								flexDirection: "column",
								borderRadius: 2.5,
								overflow: "hidden",
								mt: 0.25,
								bgcolor:
									theme.palette.mode === "dark"
										? alpha(theme.palette.common.white, 0.02)
										: alpha(theme.palette.primary.main, 0.018),
								border: `1px solid ${alpha(
									theme.palette.divider,
									theme.palette.mode === "dark" ? 0.3 : 0.7,
								)}`,
								"& .MuiDataGrid-root": {
									flex: 1,
									minHeight: 0,
									height: "100%",
									border: "none",
									fontSize: theme.typography.body2.fontSize,
								},
							}}
						>
							<DataGrid
								rows={rows}
								rowHeight={40}
								columns={columns}
								onRowClick={handleKpiRowClick}
								disableColumnSelector
								disableDensitySelector
								disableColumnFilter
								paginationModel={paginationModel}
								onPaginationModelChange={setPaginationModel}
								pageSizeOptions={[5, 10, 25]}
								slots={{ toolbar: GridToolbar }}
								slotProps={{
									toolbar: {
										showQuickFilter: true,
										quickFilterProps: { debounceMs: 300 },
									},
								}}
								sx={{
									"--DataGrid-headerHeight": 44,
									bgcolor: "transparent",
									"& .MuiDataGrid-toolbarContainer": {
										px: 1,
										py: 0.9,
										borderBottom: `1px solid ${alpha(
											theme.palette.divider,
											0.7,
										)}`,
										bgcolor:
											theme.palette.mode === "dark"
												? alpha(theme.palette.common.white, 0.015)
												: alpha(theme.palette.common.white, 0.7),
										"& .MuiButton-root": {
											borderRadius: 2,
											textTransform: "none",
											fontWeight: 700,
										},
										"& .MuiInputBase-root": {
											borderRadius: 2.5,
											bgcolor:
												theme.palette.mode === "dark"
													? alpha(theme.palette.common.white, 0.04)
													: theme.palette.background.paper,
										},
									},
									"& .MuiDataGrid-columnHeaders": {
										bgcolor: alpha(theme.palette.primary.main, 0.05),
										borderBottom: `1px solid ${alpha(
											theme.palette.divider,
											0.9,
										)}`,
										fontSize: theme.typography.body2.fontSize,
										fontWeight: 700,
										textTransform: "uppercase",
										letterSpacing: "0.04em",
									},
									"& .MuiDataGrid-cell": {
										borderBottom: `1px solid ${alpha(
											theme.palette.divider,
											0.62,
										)}`,
										py: 0.85,
									},
									"& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
										outline: "none",
									},
									"& .MuiDataGrid-cell:focus-visible, & .MuiDataGrid-columnHeader:focus-visible": {
										boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
									},
									"& .MuiDataGrid-row": {
										transition: "background-color 140ms ease",
									},
									"& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell": {
										borderBottom: "none",
									},
									"& .MuiDataGrid-row:nth-of-type(even)": {
										bgcolor: alpha(theme.palette.primary.main, 0.02),
									},
									"& .MuiDataGrid-virtualScroller": {
										overflowX: "hidden",
										bgcolor: alpha(
											theme.palette.common.white,
											theme.palette.mode === "dark" ? 0.015 : 0.65,
										),
									},
									"& .MuiDataGrid-row:hover": {
										backgroundColor:
											theme.palette.mode === "light"
												? alpha(theme.palette.primary.main, 0.07)
												: alpha(theme.palette.primary.main, 0.18),
										cursor: "pointer",
									},
									"& .MuiDataGrid-footerContainer": {
										borderTop: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
										minHeight: 44,
										px: 0.75,
										justifyContent: "flex-end",
										bgcolor:
											theme.palette.mode === "dark"
												? alpha(theme.palette.common.white, 0.015)
												: alpha(theme.palette.common.white, 0.7),
									},
								}}
							/>
						</Box>
					</CardContent>
				</Card>
			</Grid>

			<Grid item xs={12} md={6}>
				<KpiLineRaceChartCard
					titleKey="Training.Archive.KpiTrends"
					defaultTitle="KPI chart"
					kpiStatistics={chartItems}
					field="Average"
					height={260}
					defaultFrameMs={700}
					legendTopN={12}
					periodLabel={periodLabel ?? undefined}
					startDate={startDate}
					endDate={endDate}
					selectedRange={selectedRange}
					onRangeChange={onRangeChange}
				/>
			</Grid>
		</Grid>
	);
};

export default KpiStatisticsDataGridDashboard;
